import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../common/CCUtils"
import { log } from "../../../common/Logging"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import { Buttons } from "../../generic-sprites/Buttons"
import { ExpandoSprite } from "../../generic-sprites/ExpandoSprite"
import { loadGenericAssets } from "../../generic-sprites/GenericAssets"
import { Audio } from "../../utils/Audio"
import { CameraZoom } from "../../utils/CameraZoom"
import { colorNum, ColorUtils } from "../../utils/ColorUtils"
import { PhaserUtils } from "../../utils/PhaserUtils"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { Transitions } from "../transitions/Transitions"
import { loadAssets, RealityCheckAssets, turnSloganAssetIntoAudioAsset } from "./RealityCheckAssets"
import { RealPos } from "./RealPos"

export class RealityCheckState extends Phaser.State {

    private static readonly POSTER_SCALE = 0.3
    private static readonly MAX_POSTER_SCALE = 0.5
    private static readonly LARGE_SLOGAN_SCALE = 0.65
    private static readonly SMALL_SLOGAN_SCALE = 0.42
    private static readonly TINY_SLOGAN_SCALE = 0.1

    background: BackgroundSprite
    cool: ExpandoSprite
    notCool: Phaser.Image
    coolSlogans: Phaser.Group
    liked: ExpandoSprite
    notLiked: Phaser.Image
    likedSlogans: Phaser.Group
    exciting: ExpandoSprite
    notExciting: Phaser.Image
    excitingSlogans: Phaser.Group
    doneButton: Phaser.Button

    labelY: number
    coolLabelX: number
    likedLabelX: number
    excitingLabelX: number
    coolLabel: Phaser.Image
    notCoolLabel: Phaser.Image
    likedLabel: Phaser.Image
    notLikedLabel: Phaser.Image
    excitingLabel: Phaser.Image
    notExcitingLabel: Phaser.Image
    labelGroup: Phaser.Group

    movedSlogan: Phaser.Image
    sloganPreviousX: number
    sloganPreviousY: number
    activeNotPoster: Phaser.Image
    currentCoolSlogan: Phaser.Image
    currentLikedSlogan: Phaser.Image
    currentExcitingSlogan: Phaser.Image
    coolSloganSelected = false
    likedSloganSelected = false
    excitingSloganSelected = false
    canExpandPosters = false
    slogansMovable = true

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorBlack(this.game)

        this.background = new BackgroundSprite(this.game, RealityCheckAssets.BACKGROUND)
        this.game.add.existing(this.background)
        this.background.visible = false

        Audio.add(this.game, RealityCheckAssets.INTRO_AUDIO, {
            onStop: () => {
                this.togglePosters(true)
                this.canExpandPosters = true
            }
        })

        this.makePosters()
    }

    makePosters = () => {
        log.debug("make posters called")
        this.doneButton = this.game.add.button(RealPos.doneButtonX(), RealPos.doneButtonY(),
            RealityCheckAssets.DONE_BUTTON, null, null, 1, 0, 1)
        Scale.scaleSprite(this.doneButton, 0.2)

        const coolTuple = this.makeNotPoster(RealityCheckAssets.NOT_COOL, RealityCheckAssets.COOL_SLOGAN_DEFAULT)
        this.notCool = coolTuple[0]
        this.currentCoolSlogan = coolTuple[1]
        this.cool = new ExpandoSprite(this.game, 0, 0, RealityCheckAssets.COOL)
        this.cool.name = "cool"

        this.coolSlogans = this.game.add.group()
        this.addSlogans(this.coolSlogans, this.cool, this.currentCoolSlogan,
            RealityCheckAssets.COOL_SLOGAN_1,
            RealityCheckAssets.COOL_SLOGAN_2,
            RealityCheckAssets.COOL_SLOGAN_3)

        const likedTuple = this.makeNotPoster(RealityCheckAssets.NOT_LIKED, RealityCheckAssets.LIKED_SLOGAN_DEFAULT)
        this.notLiked = likedTuple[0]
        this.currentLikedSlogan = likedTuple[1]
        this.liked = new ExpandoSprite(this.game, 0, 0, RealityCheckAssets.LIKED)
        this.liked.name = "liked"

        this.likedSlogans = this.game.add.group()
        this.addSlogans(this.likedSlogans, this.liked, this.currentLikedSlogan,
            RealityCheckAssets.LIKED_SLOGAN_1,
            RealityCheckAssets.LIKED_SLOGAN_2,
            RealityCheckAssets.LIKED_SLOGAN_3)

        const excitingTuple = this.makeNotPoster(RealityCheckAssets.NOT_EXCITING, RealityCheckAssets.EXCITING_SLOGAN_DEFAULT)
        this.notExciting = excitingTuple[0]
        this.currentExcitingSlogan = excitingTuple[1]
        this.exciting = new ExpandoSprite(this.game, 0, 0, RealityCheckAssets.EXCITING)
        this.exciting.name = "exciting"

        this.excitingSlogans = this.game.add.group()
        this.addSlogans(this.excitingSlogans, this.exciting, this.currentExcitingSlogan,
            RealityCheckAssets.EXCITING_SLOGAN_1,
            RealityCheckAssets.EXCITING_SLOGAN_2,
            RealityCheckAssets.EXCITING_SLOGAN_3)

        this.clickToZoomPoster(this.cool, this.liked, this.exciting, this.notCool, this.currentCoolSlogan, this.coolSlogans, RealityCheckAssets.NOT_COOL_AUDIO)
        this.clickToZoomPoster(this.liked, this.cool, this.exciting, this.notLiked, this.currentLikedSlogan, this.likedSlogans,
            RealityCheckAssets.NOT_LIKED_AUDIO)
        this.clickToZoomPoster(this.exciting, this.liked, this.cool, this.notExciting, this.currentExcitingSlogan, this.excitingSlogans,
            RealityCheckAssets.NOT_EXCITING_AUDIO)

        this.reset()

        this.labelY = this.cool.y + this.cool.height

        this.labelGroup = this.game.add.group()

        this.coolLabel = this.game.add.image(0, 0, RealityCheckAssets.COOL_SLOGAN_OPPOSITE, null, this.labelGroup)
        Scale.scaleSprite(this.coolLabel, RealityCheckState.TINY_SLOGAN_SCALE)
        Sprites.centerSpriteInSprite(this.cool, this.coolLabel)
        this.coolLabelX = this.coolLabel.centerX
        this.coolLabel.y = this.labelY

        this.likedLabel = this.game.add.image(0, 0, RealityCheckAssets.LIKED_SLOGAN_OPPOSITE, null, this.labelGroup)
        Scale.scaleSprite(this.likedLabel, RealityCheckState.TINY_SLOGAN_SCALE)
        Sprites.centerSpriteInSprite(this.liked, this.likedLabel)
        this.likedLabelX = this.likedLabel.centerX
        this.likedLabel.y = this.labelY

        this.excitingLabel = this.game.add.image(0, 0, RealityCheckAssets.EXCITING_SLOGAN_OPPOSITE, null, this.labelGroup)
        Scale.scaleSprite(this.excitingLabel, RealityCheckState.TINY_SLOGAN_SCALE)
        Sprites.centerSpriteInSprite(this.exciting, this.excitingLabel)
        this.excitingLabelX = this.excitingLabel.centerX
        this.excitingLabel.y = this.labelY

        this.togglePosters(CCUtils.debug)
    }

    reset = () => {
        log.debug("reset called")

        this.notCool.alpha = 0
        this.notLiked.alpha = 0
        this.notExciting.alpha = 0
        Sprites.centerInWorld(this.notCool)
        Sprites.centerInWorld(this.notLiked)
        Sprites.centerInWorld(this.notExciting)

        this.cool.unexpand()
        this.liked.unexpand()
        this.exciting.unexpand()
        this.cool.alpha = 1
        this.liked.alpha = 1
        this.exciting.alpha = 1
        this.cool.visible = true
        this.liked.visible = true
        this.exciting.visible = true

        this.doneButton.tint = colorNum.gray
        this.doneButton.input.enabled = false
        this.doneButton.visible = false

        this.cool.changeScale(RealityCheckState.POSTER_SCALE)
        this.liked.changeScale(RealityCheckState.POSTER_SCALE)
        this.exciting.changeScale(RealityCheckState.POSTER_SCALE)

        Sprites.centerInWorld(this.cool)
        Sprites.centerInWorld(this.liked)
        Sprites.centerInWorld(this.exciting)

        const posterOffset = this.cool.width + Scale.width() * 0.025
        this.cool.x -= posterOffset
        this.exciting.x += posterOffset

        this.togglePosters(true)

        this.movedSlogan = null
    }

    clickToZoomPoster = (
        clickToZoom: ExpandoSprite, fadeOut: ExpandoSprite, fadeOut2: ExpandoSprite,
        fadeIn: Phaser.Image, fadeIn2: Phaser.Image, sloganGroup: Phaser.Group, badAudioAsset: string
    ) => {
        clickToZoom.addOnClick(() => {
            if (this.canExpandPosters) {
                log.debug("click to zoom poster clicked.")
                this.labelGroup.forEach((image: Phaser.Image) => {
                    image.visible = false
                })

                this.togglePosters(false)
                clickToZoom.tint = colorNum.white
                this.activeNotPoster = fadeIn
                Sprites.fadeOut(fadeOut)
                Sprites.fadeOut(fadeOut2)
                CameraZoom.zoomSprite(clickToZoom, this.notCool.x, this.notCool.y, RealityCheckState.MAX_POSTER_SCALE, 1000, () => {
                    // Visible = false turns off clicks and stuff, but not alpha = 0.
                    fadeOut.alpha = 1
                    fadeOut.visible = false
                    fadeOut2.alpha = 1
                    fadeOut2.visible = false
                    fadeIn.alpha = 1
                    fadeIn2.alpha = 1
                    if (clickToZoom.input) {
                        clickToZoom.input.enabled = false
                    }

                    Audio.add(this.game, badAudioAsset)

                    const timer = this.game.time.create()
                    timer.add(2000, () => {
                        Sprites.fadeOut(clickToZoom, 1000)
                    })
                    timer.add(4000, () => {
                        const posterPadding = Scale.width() * 0.02
                        const moveSloganTo = posterPadding + fadeIn2.x - fadeIn.x
                        CameraZoom.moveSprite(fadeIn, posterPadding, null)
                        CameraZoom.moveSprite(fadeIn2, moveSloganTo, null)
                    })
                    timer.add(5000, () => {
                        sloganGroup.visible = true
                        this.doneButton.visible = true
                        Audio.add(this.game, RealityCheckAssets.CLICK_SLOGAN, {
                            onStop: () => {
                                sloganGroup.forEach((slogan: Phaser.Image) => {
                                    slogan.input.enabled = true
                                }, this)
                            }
                        })
                    })
                    timer.start()
                })
            }
        })
    }

    addSlogans = (group: Phaser.Group, grayOut: ExpandoSprite, defaultSlogan: Phaser.Image, ...slogans: Array<string>) => {
        log.debug("add slogans called")
        group.visible = false
        let currentY = RealPos.slogansY()
        const sloganImages: Array<Phaser.Image> = []
        slogans.forEach((slogan: string) => {
            const sloganImage = this.game.add.image(RealPos.slogansX(), currentY, slogan, null, group)
            Scale.scaleSprite(sloganImage, RealityCheckState.LARGE_SLOGAN_SCALE)
            sloganImages.push(sloganImage)
            Buttons.makeSpriteButton(sloganImage)
            sloganImage.input.enabled = false
            sloganImage.events.onInputDown.add(() => {
                if (this.slogansMovable) {
                    log.debug("slogan image clicked to be moved.")
                    if (this.movedSlogan === sloganImage) {
                        log.debug("this sloganImage was already moved, so don't do anything.")
                        return
                    }

                    log.info("Moving infoText " + slogan)
                    if (grayOut.name === "cool") {
                        this.coolSloganSelected = true
                        this.currentCoolSlogan = sloganImage
                    } else if (grayOut.name === "liked") {
                        this.likedSloganSelected = true
                        this.currentLikedSlogan = sloganImage
                    } else {
                        this.excitingSloganSelected = true
                        this.currentExcitingSlogan = sloganImage
                    }

                    this.doneButton.tint = colorNum.gray
                    this.doneButton.input.enabled = false
                    sloganImages.forEach((sloganImg: Phaser.Image) => {
                        // Now that moving and slogan voicing is done, turn all the buttons back on.
                        sloganImg.input.enabled = false
                    })

                    if (this.movedSlogan) {
                        CameraZoom.moveSprite(this.movedSlogan, this.sloganPreviousX, this.sloganPreviousY)
                    } else {
                        Sprites.fadeOut(defaultSlogan)
                    }

                    this.movedSlogan = sloganImage
                    this.sloganPreviousX = sloganImage.x
                    this.sloganPreviousY = sloganImage.y

                    const sloganMoveTween = CameraZoom.moveSprite(sloganImage,
                        Sprites.centerCoorForSpriteInSprite(
                            this.activeNotPoster.x, this.activeNotPoster.width, sloganImage.width),
                        this.activeNotPoster.y + Scale.height() * 0.02)
                    sloganMoveTween.onComplete.add(() => {
                        Audio.add(this.game, turnSloganAssetIntoAudioAsset(slogan), {
                            onStop: () => {
                                sloganImages.forEach((sloganImg: Phaser.Image) => {
                                    // Now that moving and slogan voicing is done, turn all the buttons back on.
                                    sloganImg.input.enabled = true
                                })
                                this.doneButton.tint = colorNum.white
                                this.doneButton.input.enabled = true
                            }
                        })
                    })

                    this.doneButton.onInputDown.addOnce(() => {
                        log.debug("Done button clicked for slogan.")
                        sloganImages.forEach((sloganImg) => {
                            sloganImg.visible = false
                        })
                        this.labelGroup.forEach((image: Phaser.Image) => {
                            image.visible = true
                        })
                        if (grayOut.name === "cool") {
                            this.coolLabel.destroy()
                            this.notCoolLabel = this.game.add.image(0, 0, RealityCheckAssets.COOL_SLOGAN_DEFAULT, null, this.labelGroup)
                            Scale.scaleSprite(this.notCoolLabel, RealityCheckState.SMALL_SLOGAN_SCALE)
                            this.notCoolLabel.centerX = this.coolLabelX
                            this.notCoolLabel.y = this.labelY
                        } else if (grayOut.name === "liked") {
                            this.likedLabel.destroy()
                            this.notLikedLabel = this.game.add.image(0, 0, RealityCheckAssets.LIKED_SLOGAN_DEFAULT, null, this.labelGroup)
                            Scale.scaleSprite(this.notLikedLabel, RealityCheckState.SMALL_SLOGAN_SCALE)
                            this.notLikedLabel.centerX = this.likedLabelX
                            this.notLikedLabel.y = this.labelY
                        } else {
                            this.excitingLabel.destroy()
                            this.notExcitingLabel = this.game.add.image(0, 0, RealityCheckAssets.EXCITING_SLOGAN_DEFAULT, null, this.labelGroup)
                            Scale.scaleSprite(this.notExcitingLabel, RealityCheckState.SMALL_SLOGAN_SCALE)
                            this.notExcitingLabel.centerX = this.excitingLabelX
                            this.notExcitingLabel.y = this.labelY
                        }
                        grayOut.grayOut()
                        this.reset()

                        if (this.coolSloganSelected && this.likedSloganSelected && this.excitingSloganSelected) {
                            this.done()
                        }
                    })
                }
            })
            currentY += sloganImage.height
        })
    }

    makeNotPoster = (asset: string, slogan: string): [Phaser.Image, Phaser.Image] => {

        const not = this.game.add.image(0, 0, asset)
        Scale.scaleSprite(not, RealityCheckState.MAX_POSTER_SCALE)
        Sprites.centerInWorld(not)
        not.alpha = 0

        const notSlogan = this.game.add.image(0, 0, slogan)
        Scale.scaleSprite(notSlogan, RealityCheckState.LARGE_SLOGAN_SCALE)
        Sprites.centerInWorldXOnly(notSlogan)
        notSlogan.y = not.y
        notSlogan.alpha = 0

        return [not, notSlogan]
    }

    togglePosters = (on: boolean) => {
        log.debug("toggling posters to " + on)
        this.cool.on = this.cool.isGrayedOut() ? false : on
        this.liked.on = this.liked.isGrayedOut() ? false : on
        this.exciting.on = this.exciting.isGrayedOut() ? false : on
    }

    done = () => {
        log.debug("In done.")
        this.background.visible = true

        this.togglePosters(false)
        this.slogansMovable = false
        Scale.scaleSprite(this.notCool, RealityCheckState.POSTER_SCALE)
        Scale.scaleSprite(this.notLiked, RealityCheckState.POSTER_SCALE)
        Scale.scaleSprite(this.notExciting, RealityCheckState.POSTER_SCALE)
        this.notCool.x = this.cool.x
        this.notCool.y = this.cool.y
        this.notLiked.x = this.liked.x
        this.notLiked.y = this.liked.y
        this.notExciting.x = this.exciting.x
        this.notExciting.y = this.exciting.y
        this.notCool.alpha = 1
        this.notLiked.alpha = 1
        this.notExciting.alpha = 1

        const defaultSloganY = this.notCool.y + this.notCool.height
        const defaultSloganScale = RealityCheckState.SMALL_SLOGAN_SCALE

        const smallSloganY = this.notCool.y + Scale.height() * 0.02

        const updateSlogan = (currentSlogan: Phaser.Image, sloganExpando: ExpandoSprite, asset: RealityCheckAssets, centerIn: Phaser.Image) => {
            Scale.scaleSprite(currentSlogan, RealityCheckState.SMALL_SLOGAN_SCALE)
            currentSlogan.y = smallSloganY
            currentSlogan.x = Sprites.centerCoorForSpriteInSprite(sloganExpando.x, sloganExpando.width, currentSlogan.width)
            currentSlogan.alpha = 1
            currentSlogan.visible = true
            const defaultCoolSlogan = this.game.add.image(0, 0, asset)
            Scale.scaleSprite(defaultCoolSlogan, defaultSloganScale)
            Sprites.centerSpriteInSprite(centerIn, defaultCoolSlogan)
            defaultCoolSlogan.y = defaultSloganY
            sloganExpando.visible = false
        }

        updateSlogan(this.currentCoolSlogan, this.cool, RealityCheckAssets.COOL_SLOGAN_DEFAULT, this.notCool)
        updateSlogan(this.currentLikedSlogan, this.liked, RealityCheckAssets.LIKED_SLOGAN_DEFAULT, this.notLiked)
        updateSlogan(this.currentExcitingSlogan, this.exciting, RealityCheckAssets.EXCITING_SLOGAN_DEFAULT, this.notExciting)

        Audio.add(this.game, RealityCheckAssets.VAPERS_NOT_COOL, {
            volume: 0.9,
            onStop: () => {
                Buttons.create(this.game, "Next", () => {
                        Transitions.startOutro(this.game, ActivityKey.REALITY_CHECK)
                    },
                    {x: RealPos.nextButtonX(), y: RealPos.nextButtonY()}
                )
            }
        })
    }
}
